import { Children, Fragment, isValidElement, useCallback, useEffect, useMemo, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { useIsomorphicEffect } from '@/hooks/useIsomorphicEffect/useIsomorphicEffect';
import { useResizeHeight } from '@/hooks/useResizeHeight/useResizeHeight';
import { useScroll } from '@/hooks/useScroll/useScroll';
import { BREAKPOINT_LAPTOP } from '@/styles/_breakpoints';
import { cx } from '@/utils/cx';

import { HeaderBottom } from './Bottom/HeaderBottom';
import styles from './Header.module.scss';
import { ActionProvider, BreakpointProvider, ScrollProvider, useIsDesktop } from './HeaderContext';

import type { HeaderCssColor } from './types';
import type { ScrollState } from '@/hooks/useScroll/useScroll';

/**
 * 호스트가 root 컨테이너에 임의의 `data-*` attribute 를 전달할 수 있도록 허용하는 index signature.
 * `onClick`, `role` 등 컴포넌트 의도와 충돌할 수 있는 일반 HTML prop 은 받지 않는다.
 * 라이브러리 내부 prefix(`data-core-ui-*`) 와 겹치지 않는 이름을 사용해야 한다.
 */
type DataAttributes = Record<`data-${string}`, string | number | boolean | undefined>;

export interface HeaderProps extends DataAttributes {
  children: ReactNode;
  /** 모든 Header.Action 클릭 시 호출되는 중앙 핸들러 */
  onAction?: (action: string, meta: Record<string, unknown>, originalEvent: React.MouseEvent) => void;
  /**
   * showOnScroll / direction 트리거 기준 scrollY. `useScroll` 의 hysteresis 도 이 값과 동일하게
   * 동작하므로(방향 전환에 필요한 변화량) **0 으로 두면 1px 변화에도 direction 이 토글된다**.
   * Bottom collapse/reveal 모드 사용 시 scroll-anchoring 보정으로 인한 false-positive 토글을
   * 피하려면 Bottom 슬롯 높이보다 충분히 크게 설정해야 한다.
   *
   * 기본값 52 (모바일 Top 높이) — 일반적 사용 케이스에서 안정. 수정이 필요한 경우 명시적으로 전달.
   */
  scrollThreshold?: number;
  /** 헤더 높이 변경 시 호출 (Bottom 슬롯 등으로 가변 높이일 때 유용) */
  onHeightChange?: (height: number) => void;
  /** 스크롤 상태 변경 시 호출 */
  onScrollChange?: (state: ScrollState) => void;
  /**
   * 데스크탑/모바일 전환 시 호출. `isDesktop` 은 뷰포트가 임계값 **이상**인가다.
   *
   * 여기서 말하는 *데스크탑* 은 **레이아웃 상태**이지 디자인 토큰 이름이 아니다 —
   * 임계값의 정본 이름은 `laptop`(1024) 이다. 자세히는 `styles/_breakpoints.ts` 참조.
   */
  onBreakpointChange?: (isDesktop: boolean) => void;
  /**
   * 데스크탑 레이아웃으로 전환되는 뷰포트 폭. 기본값 `BREAKPOINT_LAPTOP`(1024).
   *
   * **정본(nol-design-tokens)은 1024 를 `laptop` 이라 부른다.** prop 이름이 `desktop` 인 것은
   * core-ui 가 그 폭부터 데스크탑 레이아웃을 쓰기 때문이고, 토큰 이름과는 다른 층이다.
   * (정본의 `desktop` 1320 은 컨테이너 바깥 폭 맥락의 값이라 전환점이 아니다.)
   */
  desktopBreakpoint?: number;
  /**
   * Header 배경색. 미지정 시 디자인 시스템 default ($static-white).
   * 명시 시 root inline style 로 `--core-ui-header-bg` 변수에 inject — className 의 CSS 변수보다 우선.
   *
   * `'transparent'` 자동완성 hint + 임의 CSS color value 허용.
   */
  background?: HeaderCssColor;
  /**
   * Header 하단 border-color. 미지정 시 디자인 시스템 default ($opacity-black-5).
   * 명시 시 root inline style 로 `--core-ui-header-border-color` 변수에 inject.
   *
   * `'transparent'` 로 border 시각적 제거 (border 자체는 ::after 로 그대로 존재).
   */
  borderColor?: HeaderCssColor;
  /**
   * 스크롤 시 그림자/border 강조 효과 표시 여부. default `true`.
   * `false` 면 `--core-ui-header-scroll-effect-display:none` inject — scrolled 상태에서도 효과 미발현.
   *
   * 미래에 부분 variant (`'shadow-only'` 등) 필요 시 `boolean | string` union 으로 확장 (minor BC).
   */
  scrollEffect?: boolean;
  /**
   * fixed header 의 z-index. 미지정 시 디자인 시스템 default (30).
   * 명시 시 root inline style 로 `--core-ui-header-z-index` 변수에 inject — className 의 CSS 변수보다 우선.
   *
   * 호스트 layer 시스템에서 modal / overlay 위에 헤더를 배치해야 할 때 사용.
   * 정적 선언 / `calc()` / 디자인 토큰 `var()` 참조가 필요하면 CSS 변수 직접 선언 권장.
   */
  zIndex?: number;
  /**
   * 헤더의 *강조도* — brand presence 가 부각된 메인성 페이지(홈 등) 와 utility 성격의 sub page 분기.
   *
   * - `'default'` (기본): sub page 표준.
   * - `'hero'`: 메인성 페이지 — 모바일에서 logo 자동 large + Top padding 확장 (`4px 20px`).
   *
   * # 왜 모바일 한정인가
   *
   * PC default 가 이미 hero 수준에 도달해 있음 — padding `2px 40px`, Logo `64×24`.
   * 반면 모바일 default 는 sub page 용으로 compact (`4px 10px`, Logo `46.5×17`).
   * `variant="hero"` 는 *모바일의 compact 를 풀어 PC 의 hero 수준에 맞추는* 의미라,
   * PC 값들은 두 variant 가 동일한 게 의도된 동작. (variant=hero 는 모바일이 PC 와 같아지는 prop)
   *
   * 추후 디자인팀이 PC 에도 differentiated hero spec 을 추가하면 자연스럽게 확장 가능 (BC additive).
   *
   * # 호스트 책임
   *
   * variant 가 변경하지 않는 sibling spacing (예: logo↔content 사이 추가 여백, gap 등) 은
   * 호스트가 SCSS 로 직접 제어.
   */
  variant?: 'default' | 'hero';
  /**
   * Header positioning 전략.
   *
   * - `'fixed'` (default): viewport 에 고정 (`position: fixed`). spacer div 가 컨텐츠 offset 을
   *   자동 처리. `Header.Bottom` 사용 시 호스트가 `height` prop / CSS 변수를 줘야 SSR 첫 paint
   *   부터 spacer 가 정확해 CLS 없음. 부모 element 제약 없음.
   * - `'sticky'`: 페이지 자연 흐름에 합류 (`position: sticky; top: 0`). spacer 불필요 → SSR
   *   첫 paint CLS 0. 단, **조상 element 에 sticky 컨텍스트를 깨는 속성이 있으면 동작 안 함**:
   *   `overflow:hidden|scroll|auto`, `transform`, `filter`, `perspective`, `contain:paint|layout|strict`,
   *   `will-change:transform/filter/perspective`. dev 환경에서 자동 detect + 콘솔 경고.
   *
   * sticky 모드에선 `Header.Bottom.height` prop 은 의미 없음 (spacer 가 없으므로) — 명시해도 무해.
   * `Header.Bottom` 의 `collapse` / `reveal` / `fixed` behavior 는 transform 기반이라 sticky 모드에서도 정상 동작.
   *
   * # sticky + Header.Bottom collapse/reveal — 호스트 책임 권장
   *
   * sticky 모드는 wrapper 가 layout flow 안에 있어서, Bottom collapse/reveal 의 wrapper height
   * 변동이 page layout 변동으로 이어진다. 이걸 브라우저의 scroll-anchoring 이 감지해서
   * `scrollY` 를 자동 보정 → useScroll 의 direction 판정 흔들림 → Bottom 또 토글 → 무한 jitter
   * loop 가 만들어진다.
   *
   * 라이브러리는 root 가 layout flow 안에 있는 sticky 구조 자체를 회피할 수 없으므로, 호스트가
   * 스크롤 컨테이너 (보통 `body` 또는 페이지 main 요소) 에 `overflow-anchor: none` 을 선언해
   * anchor 발화 자체를 차단해야 한다.
   *
   * ```css
   * body { overflow-anchor: none; }
   * ```
   */
  position?: 'fixed' | 'sticky';
  /** root 컨테이너에 적용할 커스텀 클래스. CSS 변수 선언 시 wrapper + spacer 모두에 cascade. */
  className?: string;
  /** root 컨테이너에 적용할 inline style. CSS 변수 주입 시 wrapper + spacer 모두에 cascade. */
  style?: CSSProperties;
}

/**
 * React.Children.forEach는 Fragment 자식을 펼치지 않음.
 * Fragment로 묶인 Header.Bottom 전체가 topChildren으로 가면 <header> 안에 렌더되어 선언 순서/DOM이 어긋남.
 */
function flattenFragments(children: ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === Fragment) {
      out.push(...flattenFragments((child.props as { children?: ReactNode }).children));
    } else {
      out.push(child);
    }
  });
  return out;
}

/** children에서 Header.Bottom 과 나머지를 분리 (중첩 Fragment 재귀 펼침) */
function splitChildren(children: ReactNode): { topChildren: ReactNode[]; bottomChildren: ReactNode[] } {
  const topChildren: ReactNode[] = [];
  const bottomChildren: ReactNode[] = [];

  for (const child of flattenFragments(children)) {
    if (isValidElement(child) && child.type === HeaderBottom) {
      bottomChildren.push(child);
    } else {
      topChildren.push(child);
    }
  }

  return { topChildren, bottomChildren };
}

interface BottomHeightSum {
  mobile: number;
  desktop: number;
  /** prop 으로 height 가 지정된 Header.Bottom 의 개수 — dev warning 용 */
  count: number;
}

/**
 * children 을 재귀로 순회하며 `Header.Bottom` 의 `height` prop 을 합산.
 *
 * 인지 범위: 직접 자식, Fragment.
 * 호스트 자체 컴포넌트로 `Header.Bottom` 을 감싸는 합성 케이스에서는 element type 매칭이
 * 실패하여 합산에 포함되지 않는다 — 그 경우 호스트가 CSS 변수를 직접 선언해야 한다.
 *
 * React render 시점에 동기적으로 실행되므로 SSR 첫 paint 부터 정확한 합계가 반영된다.
 */
function sumBottomHeights(children: ReactNode): BottomHeightSum {
  const sum: BottomHeightSum = { mobile: 0, desktop: 0, count: 0 };

  const visit = (node: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) return;
      if (child.type === Fragment) {
        visit((child.props as { children?: ReactNode }).children);
        return;
      }
      // 직접 참조 비교 — displayName 문자열 비교는 minification 시 mangling 또는
      // displayName 미선언 시 false-match 위험이 있어 사용하지 않는다.
      if (child.type === HeaderBottom) {
        const height = (child.props as { height?: { mobile: number; desktop: number } }).height;
        if (height) {
          sum.mobile += height.mobile;
          sum.desktop += height.desktop;
          sum.count += 1;
        }
      }
    });
  };

  visit(children);
  return sum;
}

export const Header = ({ desktopBreakpoint, onBreakpointChange, ...props }: HeaderProps) => {
  if (process.env.NODE_ENV !== 'production' && desktopBreakpoint !== undefined) {
    // eslint-disable-next-line no-console -- dev-only warning
    console.warn(
      '[core-ui] `desktopBreakpoint` activates JS-attribute mode which has SSR hydration flash. ' +
        'Default mode uses CSS @media (flash-free). Use only when design override is required.',
    );
  }
  return (
    <BreakpointProvider onChange={onBreakpointChange} value={desktopBreakpoint ?? BREAKPOINT_LAPTOP}>
      <HeaderInner hasBreakpointOverride={desktopBreakpoint !== undefined} {...props} />
    </BreakpointProvider>
  );
};

type HeaderInnerProps = Omit<HeaderProps, 'desktopBreakpoint' | 'onBreakpointChange'> & {
  hasBreakpointOverride: boolean;
};

const HeaderInner = ({
  children,
  onAction,
  onHeightChange,
  onScrollChange,
  scrollThreshold = 52,
  background,
  borderColor,
  scrollEffect,
  zIndex,
  variant,
  position = 'fixed',
  className,
  style,
  hasBreakpointOverride,
  ...dataAttrs
}: HeaderInnerProps) => {
  const isDesktop = useIsDesktop();
  const { isScrolled, direction: scrollDirection } = useScroll(scrollThreshold, onScrollChange);

  const spacerRef = useRef<HTMLDivElement>(null);
  const {
    ref: wrapperRef,
    height: measuredHeight,
    reset: resetMeasure,
  } = useResizeHeight<HTMLDivElement>({
    onHeightChange,
  });

  // 브레이크포인트 전환 시 stale inline height 를 비워 CSS min-height (@media + 변수 폴백) 에 인계.
  // desktop→mobile 전환에서만 의미: stale 118 > mobile min-height 52 → max() 가 stale 을 이김.
  //
  // 단순히 inline 만 비우면 race: 다음 ResizeObserver 측정값이 이전 state (118) 와 같으면
  // setHeight skip → React 가 inline 을 재적용 못 함 → spacer 가 CSS min-height 만으로 그려져
  // wrapper 보다 작아짐. hydration 직후 isDesktop SSR(false)→client(true) 전환에서 간헐적 stuck 재현.
  //
  // 해결: inline 비움 + height state 도 0 으로 reset. 다음 measure 가 무조건 setHeight 를 호출해
  // inline 이 재적용되도록 보장.
  useIsomorphicEffect(() => {
    if (spacerRef.current) {
      spacerRef.current.style.height = '';
    }
    resetMeasure();
  }, [isDesktop, resetMeasure]);

  // Header.Bottom 의 height prop 을 children 순회로 합산.
  // 아래 dev warning useEffect 들이 deps 로 `bottomHeights.count` 를 참조하므로 사전 정의 필요.
  // React render 시점에 실행되므로 SSR HTML 의 root inline style 에 박혀나가 첫 paint 부터 spacer 가 정확.
  // 합성 케이스(호스트 컴포넌트로 Header.Bottom 감쌈) 에서는 합산 누락 → host 가 CSS 변수 폴백.
  const bottomHeights = useMemo(() => sumBottomHeights(children), [children]);

  // Dev warning — mount 후 wrapper 와 spacer 의 초기 높이 비교.
  // wrapper > spacer + 임계치(5px) 면 Bottom 의 layout 공간을 spacer 가 못 따라감.
  // 호스트가 prop / CSS 변수 둘 다 누락했거나 합성 케이스(자체 컴포넌트로 Header.Bottom 감쌈)에서
  // children inspection 이 실패했을 때 발생. CLS 위험 알림.
  //
  // sticky 모드는 spacer 자체가 없으므로 검증 skip.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (position === 'sticky') return;
    const wrapperEl = wrapperRef.current;
    const spacerEl = spacerRef.current;
    if (!wrapperEl || !spacerEl) return;
    if (isScrolled) return; // collapsed 상태는 wrapper < spacer 정상 — 비교 의미 없음

    const wrapperH = wrapperEl.offsetHeight;
    const spacerH = spacerEl.offsetHeight;
    if (wrapperH - spacerH <= 5) return;

    if (bottomHeights.count === 0) {
      // eslint-disable-next-line no-console -- dev-only warning
      console.warn(
        '[core-ui] Header.Bottom 이 렌더링되었지만 height prop 또는 ' +
          '`--core-ui-header-bottom-height-mobile|desktop` CSS 변수가 선언되지 않았습니다. ' +
          'spacer 가 Bottom 높이를 반영하지 못해 CLS 가 발생할 수 있습니다. ' +
          '대안: `<Header position="sticky">` 로 전환하면 spacer 자체가 사라져 CLS 가 본질적으로 해결됩니다.',
      );
    } else {
      // eslint-disable-next-line no-console -- dev-only warning
      console.warn(
        '[core-ui] 일부 Header.Bottom 이 host 컴포넌트로 감싸진 것으로 보입니다 ' +
          '(wrapper 높이 > spacer 높이). compound sub-component 는 `<Header>` 의 직접 자식에 두고, ' +
          '도메인 로직은 Header.Bottom 의 children 으로만 분리하세요. ' +
          '또는 누락된 만큼을 CSS 변수로 보강해주세요.',
      );
    }
    // mount 1회만 실행 (deps 빈 배열) — 재측정은 사용자 상호작용 후 자연스럽게 일어남
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dev warning — sticky 모드 + Header.Bottom.height 명시 조합.
  // sticky 모드는 spacer 가 없어 height prop 이 의미 없음 (spacer min-height 폴백에 합산되는 게 prop 의 역할).
  // 호스트가 인지하지 못한 채 fixed 모드 코드 그대로 sticky 전환했을 가능성 알림.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (position !== 'sticky') return;
    if (bottomHeights.count === 0) return;
    // eslint-disable-next-line no-console -- dev-only warning
    console.warn(
      '[core-ui] Header position="sticky" + Header.Bottom 의 `height` prop 명시 — sticky 모드는 spacer 가 없어 ' +
        'height prop 이 효과 없습니다. 안전하게 제거 가능합니다.',
    );
  }, [position, bottomHeights.count]);

  // Dev warning — sticky 모드에서 조상 element 가 sticky 컨텍스트를 깨고 있으면 경고.
  // sticky 는 부모 chain 의 특정 속성에서 동작이 깨지는데, 시각적으로 *fixed 처럼 동작 안 함* /
  // *컨테이너 안에서만 sticky* 같은 어색한 동작으로 나타나 디버깅 어려움.
  // mount 후 1회 부모 chain 순회, 첫 깨는 element 발견 시 콘솔에 정보 출력.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (position !== 'sticky') return;
    // body 까지 포함해 순회 — `body { overflow-x: clip }` 같은 global reset 도 sticky 를 깬다.
    // documentElement (html) 는 page scroll container 자체라 제외.
    let el: HTMLElement | null = wrapperRef.current?.parentElement ?? null;
    while (el && el !== document.documentElement) {
      const cs = window.getComputedStyle(el);
      // overflow 는 shorthand + longhand (overflow-x / overflow-y) 모두 체크.
      // `overflow-x: clip` 같은 한 방향만 설정도 새 scroll container 를 만들어 sticky 를 깬다.
      const overflowBroken =
        (cs.overflow !== 'visible' && cs.overflow !== '') ||
        (cs.overflowX !== 'visible' && cs.overflowX !== '') ||
        (cs.overflowY !== 'visible' && cs.overflowY !== '');
      const transformBroken = cs.transform !== 'none';
      const filterBroken = cs.filter !== 'none';
      const perspectiveBroken = cs.perspective !== 'none';
      // eslint-disable-next-line prefer-named-capture-group
      const containBroken = /\b(paint|layout|strict|content)\b/.test(cs.contain);
      const willChangeBroken = /transform|filter|perspective/.test(cs.willChange);

      if (overflowBroken || transformBroken || filterBroken || perspectiveBroken || containBroken || willChangeBroken) {
        // eslint-disable-next-line no-console -- dev-only warning
        console.warn(
          '[core-ui] Header position="sticky" — ancestor 가 sticky 컨텍스트를 깨고 있습니다. ' +
            '이 요소가 새 scroll container / containing block 을 만들어 sticky 가 viewport 가 아닌 이 요소 기준이 됩니다. ' +
            '해결: 해당 ancestor 의 해당 속성을 제거하거나, `position="fixed"` 로 전환하세요.',
          {
            element: el,
            overflow: cs.overflow,
            overflowX: cs.overflowX,
            overflowY: cs.overflowY,
            transform: cs.transform,
            filter: cs.filter,
            perspective: cs.perspective,
            contain: cs.contain,
            willChange: cs.willChange,
          },
        );
        break;
      }
      el = el.parentElement;
    }
  }, [position]);

  const handleAction = useCallback(
    (action: string, meta: Record<string, unknown>, e: React.MouseEvent) => {
      onAction?.(action, meta, e);
    },
    [onAction],
  );

  const scrollValue = useMemo(() => ({ isScrolled, scrollDirection }), [isScrolled, scrollDirection]);
  const actionValue = useMemo(() => ({ onAction: handleAction }), [handleAction]);
  const { topChildren, bottomChildren } = splitChildren(children);
  // spacer 높이 우선순위: measured(inline) > CSS min-height (@media + 호스트 변수 폴백).
  // Bottom collapse / re-expand 시 jitter 방지는 CSS min-height 가 펼쳐진 헤더 높이를 유지해 자동 처리.
  const spacerStyle = useMemo(() => (measuredHeight > 0 ? { height: measuredHeight } : undefined), [measuredHeight]);

  // root inline style 에 박혀나가 SSR 첫 paint 부터 spacer 가 정확.
  // 합산 결과를 host 가 직접 선언한 CSS 변수보다 우선시 (inline > className specificity).
  const rootStyle = useMemo<CSSProperties>(() => {
    const cssVars: CSSProperties & Record<string, string> = {};
    if (bottomHeights.mobile > 0) {
      cssVars['--core-ui-header-bottom-height-mobile'] = `${bottomHeights.mobile}px`;
    }
    if (bottomHeights.desktop > 0) {
      cssVars['--core-ui-header-bottom-height-desktop'] = `${bottomHeights.desktop}px`;
    }
    // prop 우선순위: 명시 시 inline style 로 CSS 변수 inject → className 의 변수보다 specificity ↑.
    // 미지정 시 inject 생략 → 호스트 className 또는 SCSS default 가 그대로 동작 (BC 호환).
    if (background !== undefined) {
      cssVars['--core-ui-header-bg'] = background;
    }
    if (borderColor !== undefined) {
      cssVars['--core-ui-header-border-color'] = borderColor;
    }
    if (scrollEffect === false) {
      cssVars['--core-ui-header-scroll-effect-display'] = 'none';
    }
    if (zIndex !== undefined) {
      cssVars['--core-ui-header-z-index'] = String(zIndex);
    }
    return { ...style, ...cssVars };
  }, [bottomHeights.mobile, bottomHeights.desktop, style, background, borderColor, scrollEffect, zIndex]);

  return (
    <ActionProvider value={actionValue}>
      <ScrollProvider value={scrollValue}>
        {/*
         * root: 호스트 className/style 의 CSS 변수 cascade 경로.
         * wrapper(fixed) 와 spacer 모두 이 root 의 자식이므로 변수가 둘 다에 도달.
         */}
        <div className={cx(styles.root, className)} data-core-ui-position={position} style={rootStyle} {...dataAttrs}>
          <div
            className={cx(styles.wrapper, isScrolled && styles.scrolled)}
            // `desktop`|`mobile` 은 **레이아웃 상태**다(토큰 이름이 아니다). 임계값은 `BREAKPOINT_LAPTOP`.
            // SCSS 쪽 짝은 `desktop-layout` / `mobile-layout` mixin 이다.
            // eslint-disable-next-line no-nested-ternary
            data-core-ui-breakpoint={hasBreakpointOverride ? (isDesktop ? 'desktop' : 'mobile') : undefined}
            data-core-ui-header="root"
            data-core-ui-mode={hasBreakpointOverride ? 'js' : undefined}
            data-core-ui-variant={variant === 'hero' ? 'hero' : undefined}
            data-scrolled={isScrolled || undefined}
            ref={wrapperRef}
          >
            <header>{topChildren}</header>
            {Children.count(bottomChildren) > 0 && <div>{bottomChildren}</div>}
          </div>
          {/*
           * spacer: fixed 모드에서만 렌더.
           * sticky 모드는 페이지 자연 흐름이라 spacer 가 컨텐츠 와 *겹침* 만들고 (이중 spacing)
           * sticky 의 핵심 장점인 SSR 첫 paint 정확 정렬을 망친다.
           */}
          {position === 'fixed' && (
            <div
              className={styles.spacer}
              data-core-ui-header="spacer"
              ref={spacerRef}
              style={spacerStyle}
              suppressHydrationWarning
            />
          )}
        </div>
      </ScrollProvider>
    </ActionProvider>
  );
};
